import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../lib/supabase';

export default function ResourceList() {
  const { i18n } = useTranslation();
  const isFr = i18n.language === 'fr';

  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchArticles() {
      setLoading(true);
      const { data, error } = await supabase
        .from('resources')
        .select('id, slug, title, title_fr, summary, summary_fr, category, published_at, author, og_image_url')
        .eq('is_published', true)
        .order('published_at', { ascending: false }); // ⚡ Date-wise sorting (newest first)

      if (error) {
        console.error("Error fetching articles:", error);
      } else if (data) {
        setArticles(data);
      }
      setLoading(false);
    }

    fetchArticles();
  }, []);

  const pageTitle = isFr ? "Analyses de Marché & Renseignements | TariffBeater" : "Market Intelligence & Trade Insights | TariffBeater";
  const pageDesc = isFr 
    ? "Dernières analyses sur les tarifs douaniers, le commerce interprovincial et l'approvisionnement en matériaux ICI au Canada." 
    : "Latest updates on U.S.-Canada tariffs, CFTA inter-provincial trade, and Canadian commercial construction sourcing.";
  const canonical = `https://www.tariffbeater.com${isFr ? '/fr' : ''}/resources`;
  const ogImage = `https://www.tariffbeater.com/api/og?title=${encodeURIComponent(pageTitle)}&category=KNOWLEDGE+CENTER`;

  return (
    <div className="min-h-screen bg-slate-50 py-12 px-4 sm:px-6 lg:px-8">
      <Helmet>
        {/* Standard SEO */}
        <title>{pageTitle}</title>
        <meta name="description" content={pageDesc} />
        <link rel="canonical" href={canonical} />

        {/* OpenGraph / LinkedIn */}
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="TariffBeater" />
        <meta property="og:url" content={canonical} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDesc} />
        <meta property="og:image" content={ogImage} />

        {/* Twitter / X */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@tariffbeater" />
        <meta name="twitter:url" content={canonical} />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDesc} />
        <meta name="twitter:image" content={ogImage} />
      </Helmet>

      <div className="max-w-6xl mx-auto">
        {/* Header Section */}
        <div className="text-center max-w-3xl mx-auto mb-12">
          <span className="text-xs font-bold text-blue-600 uppercase tracking-widest block mb-2">
            {isFr ? "Centre de Ressources" : "Knowledge Center"}
          </span>
          <h1 className="text-3xl md:text-5xl font-black text-slate-900 tracking-tight mb-4">
            {isFr ? "Analyses & Renseignements Commerciaux" : "Market Analysis & Trade Intelligence"}
          </h1>
          <p className="text-lg text-slate-600 font-light">
            {isFr 
              ? "Restez informé des politiques tarifaires, des coûts débarqués et des stratégies d'approvisionnement local pour la construction ICI au Canada."
              : "Stay ahead of tariff shifts, landed cost fluctuations, and domestic sourcing strategies for Canadian ICI construction."}
          </p>
        </div>

        {/* Loading State */}
        {loading ? (
          <div className="text-center py-20 text-slate-500 font-medium">
            {isFr ? "Chargement des articles..." : "Loading market insights..."}
          </div>
        ) : articles.length === 0 ? (
          <div className="text-center py-20 text-slate-500 font-medium bg-white rounded-2xl border border-slate-200">
            {isFr ? "Aucun article publié pour le moment." : "No articles published yet."}
          </div>
        ) : (
          /* Article Grid sorted by Date */
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {articles.map((article) => {
              const displayTitle = (isFr && article.title_fr) ? article.title_fr : article.title;
              const displaySummary = (isFr && article.summary_fr) ? article.summary_fr : article.summary;
              const articleUrl = isFr ? `/fr/resources/${article.slug}` : `/resources/${article.slug}`;

              return (
                <article key={article.id} className="bg-white rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition-all duration-200 flex flex-col overflow-hidden">
                  <div className="p-6 flex-grow flex flex-col justify-between">
                    <div>
                      <div className="flex items-center justify-between text-xs text-slate-500 mb-3">
                        <span className="font-bold text-blue-600 uppercase">{article.category || 'Trade Insight'}</span>
                        <time dateTime={article.published_at}>
                          {new Date(article.published_at).toLocaleDateString(isFr ? 'fr-CA' : 'en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </time>
                      </div>

                      <h2 className="text-xl font-bold text-slate-900 mb-3 line-clamp-2 hover:text-blue-600 transition-colors">
                        <Link to={articleUrl}>{displayTitle}</Link>
                      </h2>

                      <p className="text-slate-600 text-sm line-clamp-3 mb-6 font-light leading-relaxed">
                        {displaySummary}
                      </p>
                    </div>

                    <div className="pt-4 border-t border-slate-100 flex items-center justify-between">
                      <span className="text-xs text-slate-400 font-medium">By {article.author}</span>
                      <Link to={articleUrl} className="text-sm font-semibold text-blue-600 hover:text-blue-800 flex items-center gap-1">
                        {isFr ? "Lire l'article" : "Read Article"} →
                      </Link>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}